import React, { Component, type ReactNode } from 'react';
import { AlertTriangle, Home, RotateCcw } from 'lucide-react';
import { Sentry } from '@/lib/sentry';

interface Props {
  children: ReactNode;
  /**
   * When true, errors are logged but the component renders nothing
   * instead of showing the error UI. Use for non-critical widgets
   * (chat, analytics) that should fail silently.
   */
  silent?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Report to Sentry with component stack context
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack ?? '' } },
      tags: { boundary: this.props.silent ? 'silent' : 'visible' },
    });

    if (this.props.silent) {
      // Silent mode: log but don't show error UI
      console.warn('[ErrorBoundary:silent] Caught and suppressed:', error.message);
    } else {
      console.error('ErrorBoundary caught:', error, errorInfo);
    }
  }

  // Reset error state when children change (e.g. navigation)
  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.children !== this.props.children) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (this.state.hasError) {
      // Silent mode: render nothing — the widget simply disappears
      if (this.props.silent) return null;

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="glass-card p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            <h1 className="font-display text-2xl font-bold mb-2">Something went wrong</h1>
            <p className="text-muted-foreground text-sm mb-6">
              An unexpected error occurred. Please try again or contact Star Line support.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors btn-primary-glow"
              >
                <RotateCcw className="w-4 h-4" /> Try Again
              </button>
              <a
                href="/"
                className="flex items-center gap-2 glass-card px-5 py-2.5 text-sm font-medium hover:bg-card transition-colors"
              >
                <Home className="w-4 h-4" /> Go Home
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

